// ---------------------------------------------------------------------------
// Shared cart-state fetch — coalesces concurrent /cart.json reads triggered
// by CartDrawer, CartTierBar, CartBundleUpsell, and CartDiscount into a
// single network request instead of one per caller.
// ---------------------------------------------------------------------------
let _cartStatePromise = null;
function getCartState() {
  if (_cartStatePromise) return _cartStatePromise;
  _cartStatePromise = fetch('/cart.json')
    .then((response) => response.json())
    .finally(() => {
      _cartStatePromise = null;
    });
  return _cartStatePromise;
}

// ---------------------------------------------------------------------------
// Shared gift-policy queue — serialises CartDrawer.enforceGiftPolicy and
// every CartTierBar instance's self-check so their corrective
// /cart/change.js and /cart/add.js calls never run concurrently.
// ---------------------------------------------------------------------------
let _giftQueue = Promise.resolve();
function enqueueGiftWork(fn) {
  _giftQueue = _giftQueue.then(fn).catch((err) => {
    console.error('[CartGift] Queue error:', err);
  });
  return _giftQueue;
}

// ---------------------------------------------------------------------------
// Shared kit/bundle quantity-update sender — retries once on a failed/non-OK
// response before giving up (see updateQuantity's _sendQuantityUpdate for the
// same pattern on the main quantity-change path).
// ---------------------------------------------------------------------------
function sendKitQuantityUpdate(cartDrawer, qtyChanges, requestId, allowRetry) {
  const body = JSON.stringify({
    updates: qtyChanges,
    sections: cartDrawer.getSectionsToRender().map((section) => section.section),
    sections_url: window.location.pathname,
  });

  fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
    .then((response) => {
      if (!response.ok) throw new Error(`Cart update request failed: ${response.status}`);
      return response.text();
    })
    .then((state) => {
      if (cartDrawer.isStaleRequest(requestId)) return;
      const parsedState = JSON.parse(state);

      cartDrawer.getSectionsToRender().forEach((section) => {
        renderCartSection(section, parsedState.sections[section.section]);
      });

      cartDrawer.disableLoading();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsedState } }));
    })
    .catch((error) => {
      if (cartDrawer.isStaleRequest(requestId)) return;

      if (allowRetry) {
        console.warn('[CartDrawer] Kit quantity update failed, retrying once:', error);
        sendKitQuantityUpdate(cartDrawer, qtyChanges, requestId, false);
        return;
      }

      console.error('[CartDrawer] Kit quantity update failed:', error);
      cartDrawer.disableLoading();
    });
}

// ---------------------------------------------------------------------------
// Section render helpers
//
// renderCartSection is the single place every AJAX cart response gets
// painted into the DOM. patchCartFooter updates only the values that can
// actually change from the server response (subtotal, package protection
// row, total, savings, discount codes) in place rather than replacing
// .cart-drawer__footer's innerHTML wholesale, so the checkout button and
// any in-flight state (e.g. a discount code being typed) survive updates
// untouched. Package protection itself is kept in sync as a real cart line
// item by assets/cart-protection-sync.js, independently of this render path.
// ---------------------------------------------------------------------------
function getSectionInnerHTML(html, selector) {
  return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
}

function renderCartSection(section, html) {
  if (!html) return;

  if (section.id === 'cart-drawer__footer') {
    patchCartFooter(html);
    return;
  }

  const container = document.getElementById(section.id);
  if (!container) return;

  const el    = container.querySelector(section.selector) || container;
  const inner = getSectionInnerHTML(html, section.selector);
  if (inner != null) el.innerHTML = inner;
}

function patchCartFooter(html) {
  const container = document.getElementById('cart-drawer__footer');
  if (!container) return;

  const newFooter  = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-drawer__footer');
  const newContent = newFooter?.querySelector('.cart-drawer__footer-content');
  const oldContent = container.querySelector('.cart-drawer__footer-content');

  // Cart just emptied out, or just went from empty to its first item — the
  // footer-content wrapper itself is appearing/disappearing, so there is
  // nothing granular to preserve. Fall back to a full swap.
  if (!newContent || !oldContent) {
    container.innerHTML = newFooter ? newFooter.innerHTML : '';
    return;
  }

  // Subtotal
  const newSubtotal = newContent.querySelector('.cart-drawer__subtotal');
  const oldSubtotal = oldContent.querySelector('.cart-drawer__subtotal');
  if (newSubtotal && oldSubtotal) oldSubtotal.textContent = newSubtotal.textContent;

  // Crossed-out "would have paid without subscription pricing" total —
  // only rendered when it's actually higher than the real subtotal, so
  // like savings it can appear, disappear, or just change value.
  const newSubtotalOriginal = newContent.querySelector('.cart-drawer__subtotal-original');
  const oldSubtotalOriginal = oldContent.querySelector('.cart-drawer__subtotal-original');
  if (newSubtotalOriginal && oldSubtotalOriginal) {
    oldSubtotalOriginal.textContent = newSubtotalOriginal.textContent;
  } else if (newSubtotalOriginal && !oldSubtotalOriginal && oldSubtotal) {
    oldSubtotal.insertAdjacentElement('beforebegin', newSubtotalOriginal);
  } else if (!newSubtotalOriginal && oldSubtotalOriginal) {
    oldSubtotalOriginal.remove();
  }

  // Savings row — only rendered once cart.total_discount > 0, so it can
  // appear, disappear, or just change value between updates.
  const newSavings = newContent.querySelector('.cart-drawer__savings-wrapper');
  const oldSavings = oldContent.querySelector('.cart-drawer__savings-wrapper');
  if (newSavings && oldSavings) {
    const newAmount = newSavings.querySelector('span');
    const oldAmount = oldSavings.querySelector('span');
    if (newAmount && oldAmount) oldAmount.textContent = newAmount.textContent;
  } else if (newSavings && !oldSavings) {
    oldContent.querySelector('.cart-drawer__subtotal-wrapper')?.insertAdjacentElement('beforebegin', newSavings);
  } else if (!newSavings && oldSavings) {
    oldSavings.remove();
  }

  // Package protection row — always present in the markup but hidden when
  // there's no coverage line item; cart-protection-sync.js adds/removes
  // that line item independently of this render pipeline, so both the
  // visibility and the amount need to track the server response here.
  const newProtection = newContent.querySelector('.cart-drawer__protection-wrapper');
  const oldProtection = oldContent.querySelector('.cart-drawer__protection-wrapper');
  if (newProtection && oldProtection) {
    oldProtection.hidden = newProtection.hidden;
    const newProtectionAmount = newProtection.querySelector('.cart-drawer__protection-total');
    const oldProtectionAmount = oldProtection.querySelector('.cart-drawer__protection-total');
    if (newProtectionAmount && oldProtectionAmount) oldProtectionAmount.textContent = newProtectionAmount.textContent;
  }

  // Grand total (products + protection) — also mirrored inside the
  // checkout button itself (.cart-drawer__checkout-total), which lives
  // inside #offset-cart-drawer-ui-offer and is otherwise never touched by
  // this surgical patch, so it needs the same value applied explicitly.
  const newTotal = newContent.querySelector('.cart-drawer__total');
  const oldTotal = oldContent.querySelector('.cart-drawer__total');
  if (newTotal && oldTotal) oldTotal.textContent = newTotal.textContent;

  const newCheckoutTotal = newContent.querySelector('.cart-drawer__checkout-total');
  const oldCheckoutTotal = oldContent.querySelector('.cart-drawer__checkout-total');
  if (newCheckoutTotal && oldCheckoutTotal) oldCheckoutTotal.textContent = newCheckoutTotal.textContent;

  // "Continue without free returns" link — like savings, only rendered
  // when there's a coverage line to skip, so it can appear or disappear
  // between updates; its text is static, so no content to patch otherwise.
  const newSkipLink = newContent.querySelector('.cart-drawer__skip-protection-link');
  const oldSkipLink = oldContent.querySelector('.cart-drawer__skip-protection-link');
  if (newSkipLink && !oldSkipLink) {
    oldContent.querySelector('#offset-cart-drawer-ui-offer')?.insertAdjacentElement('afterend', newSkipLink);
  } else if (!newSkipLink && oldSkipLink) {
    oldSkipLink.remove();
  }

  // Discount codes, inside the <cart-discount> custom element — patch the
  // list only, so the element itself (and any text mid-typing in its input)
  // is never torn down and reconstructed.
  const newDiscounts = newContent.querySelector('.cart-discount__discounts');
  const discountEl   = oldContent.querySelector('cart-discount');
  const oldDiscounts = discountEl?.querySelector('.cart-discount__discounts');
  if (discountEl) {
    if (newDiscounts && oldDiscounts) {
      oldDiscounts.innerHTML = newDiscounts.innerHTML;
    } else if (newDiscounts && !oldDiscounts) {
      discountEl.insertAdjacentElement('beforeend', newDiscounts);
    } else if (!newDiscounts && oldDiscounts) {
      oldDiscounts.remove();
    }
  }
}

// ---------------------------------------------------------------------------

class CartDrawerRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartDrawer = this.closest('cart-drawer');
      const idx = this.dataset.index;
      const drawerItem = document.querySelector(`.cart-drawer__item[data-idx="${idx}"]`);
      if (!drawerItem) return;

      const drawerItemProduct = drawerItem.dataset.cartDrawerProduct;
      const drawerItemQty = Number(
        drawerItem.querySelector('[data-cart-drawer-quantity]')?.dataset.cartDrawerQuantity || 0
      );
      const kitId = drawerItem.dataset.kit;

      if (kitId) {
        const itemsWithKitId = document.querySelectorAll(`.cart-drawer__item[data-kit="${kitId}"]`);
        const qtyChanges = {};

        itemsWithKitId.forEach((item) => {
          const itemQty = Number(
            item.querySelector('[data-cart-drawer-quantity]')?.dataset.cartDrawerQuantity || 1
          );
          const itemIdx = Number(item.dataset.idx);
          const itemProduct = item.dataset.cartDrawerProduct;
          const itemKey = item.querySelector('cart-drawer-remove-button')?.dataset.key;

          // Preserve qty for same product on a different line; otherwise decrement
          qtyChanges[itemKey] =
            itemProduct === drawerItemProduct && itemIdx !== parseInt(idx, 10)
              ? itemQty
              : itemQty - drawerItemQty < 0
              ? 0
              : itemQty - drawerItemQty;
        });

        cartDrawer.enableLoading();
        const requestId = cartDrawer.beginRequest();

        sendKitQuantityUpdate(cartDrawer, qtyChanges, requestId, true);
      } else {
        cartDrawer.updateQuantity(this.dataset.key, 0);
      }
    });
  }
}

customElements.define('cart-drawer-remove-button', CartDrawerRemoveButton);

// ---------------------------------------------------------------------------

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Elements
    this.siteHtml      = document.querySelector('[data-site-html]');
    this.siteBody      = document.querySelector('[data-site-body]');
    this.siteContainer = document.querySelector('[data-site-main]');
    this.currentScroll = null;
    this.drawer        = document.getElementById('cart-drawer');

    this.cartButton = document.querySelector('.js-open-cart');
    this.cartButton.addEventListener('click', this.handleCartClick.bind(this));

    this.closeIcon = document.getElementById('cart-drawer__close');
    this.closeIcon.addEventListener('click', this.close.bind(this));

    this.onBodyClick = this.handleBodyClick.bind(this);
    this.drawer.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    this.pageOverlayElement = document.querySelector('.page-overlay');
    this.pageOverlayElement.addEventListener('click', this.close.bind(this));

    // Quantity tracking
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0
    );

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    // Fires immediately (not debounced) so the subtotal repaints the instant
    // the user clicks +/- or types a quantity, instead of waiting on the
    // debounced /cart/change.js round trip + full section re-render below.
    // Approximates from item-level prices only, so cart-level discounts,
    // tier gifts, etc. are corrected shortly after by the real response.
    this.addEventListener('change', this.updateSubtotalOptimistically.bind(this));

    this.addEventListener('change', this.debouncedOnChange.bind(this));

    // Guards stale AJAX responses from overwriting a more recent request's result.
    this._requestId = 0;

    if (window.location.href.indexOf('?cart_redirect') >= 0) {
      this.open();
    }
  }

  // -------------------------------------------------------------------------
  // enforceGiftPolicy
  //
  // Runs on every drawer open. Replaces the old cleanupStaleFreeGifts method,
  // which bailed out early when cart-tier-bar was present — i.e. exactly when
  // enforcement was needed most.
  //
  // Covers all abuse vectors:
  //   • Gift added at qty > 1 (manually or via AJAX)
  //   • Multiple duplicate gift line items
  //   • Gift present but threshold no longer met
  //   • Stale gift after the feature has been disabled entirely
  //
  // Matches on variant ID only — does NOT rely solely on the client-supplied
  // _free_tier_gift property, which any customer can forge.
  // -------------------------------------------------------------------------
  async enforceGiftPolicy() {
    return enqueueGiftWork(() => this._enforceGiftPolicyImpl());
  }

  async _enforceGiftPolicyImpl() {
    try {
      const giftTierBar = document.querySelector('cart-tier-bar[data-gift-variant-id]');

      // Feature not configured — fall back to stale-gift cleanup only
      if (!giftTierBar) {
        await this._removeStaleFreeGifts();
        return;
      }

      const giftVariantId = parseInt(giftTierBar.getAttribute('data-gift-variant-id'), 10);
      const isQualified   = giftTierBar.hasAttribute('data-tier-qualified');

      const cartData = await getCartState();
      if (!cartData.items?.length) return;

      // Match every line item that is the gift variant, regardless of properties
      const giftLines = cartData.items.filter((item) => item.id === giftVariantId);
      if (!giftLines.length) return;

      const updates = {};

      giftLines.forEach((item, idx) => {
        if (!isQualified) {
          // Threshold not met — remove every instance
          updates[item.key] = 0;
        } else if (idx === 0) {
          // Qualified: keep the first line at exactly qty 1
          if (item.quantity !== 1) updates[item.key] = 1;
        } else {
          // Qualified but duplicate line item — remove
          updates[item.key] = 0;
        }
      });

      if (!Object.keys(updates).length) return;

      this.enableLoading();

      const body = JSON.stringify({
        updates,
        sections: this.getSectionsToRender().map((s) => s.section),
        sections_url: window.location.pathname,
      });

      const res         = await fetch(routes.cart_update_url, { ...fetchConfig(), body });
      const state       = await res.text();
      const parsedState = JSON.parse(state);

      this.getSectionsToRender().forEach((section) => {
        renderCartSection(section, parsedState.sections[section.section]);
      });

      this.disableLoading();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsedState } }));
    } catch (err) {
      console.error('[CartDrawer] enforceGiftPolicy failed:', err);
      this.disableLoading();
    }
  }

  // -------------------------------------------------------------------------
  // _removeStaleFreeGifts
  //
  // Fallback used when the feature has been disabled (no cart-tier-bar in the
  // DOM) but gift line items are still present from a previous session.
  // Uses the _free_tier_gift property as the signal since there is no variant
  // ID to match against when the tier bar is gone.
  // -------------------------------------------------------------------------
  async _removeStaleFreeGifts() {
    try {
      const cartData = await getCartState();
      if (!cartData.items?.length) return;

      const stale = cartData.items.filter((item) => item.properties?._free_tier_gift);
      if (!stale.length) return;

      const updates = {};
      stale.forEach((item) => { updates[item.key] = 0; });

      const body = JSON.stringify({
        updates,
        sections: this.getSectionsToRender().map((s) => s.section),
        sections_url: window.location.pathname,
      });

      this.enableLoading();

      const res         = await fetch(routes.cart_update_url, { ...fetchConfig(), body });
      const state       = await res.text();
      const parsedState = JSON.parse(state);

      this.getSectionsToRender().forEach((section) => {
        renderCartSection(section, parsedState.sections[section.section]);
      });

      this.disableLoading();
    } catch (err) {
      console.error('[CartDrawer] _removeStaleFreeGifts failed:', err);
      this.disableLoading();
    }
  }

  // -------------------------------------------------------------------------
  // Drawer open / close
  // -------------------------------------------------------------------------
  // skipSideEffects: true is for callers opening the drawer optimistically
  // *before* their own cart mutation has reached the server (e.g. a PDP
  // add-to-cart firing the slide-in animation while the request is still in
  // flight — see assets/vpv-buy-bar.js). enforceGiftPolicy() and the
  // "cart:updated" dispatch both read the cart's *current* server state, so
  // running them here would read stale (pre-mutation) data and — via
  // cart-protection-sync.js's listener — paint the drawer with numbers that
  // don't include the very item just being added. Those callers are
  // responsible for dispatching "cart:updated" themselves once their own
  // request actually resolves.
  open({ skipSideEffects = false } = {}) {
    this.drawer.setAttribute('aria-hidden', false);
    this.drawer.setAttribute('aria-expanded', true);
    this.scrollLockOpen();
    trapFocus(this.drawer);

    this.pageOverlayElement.classList.add('is-visible');
    document.body.addEventListener('click', this.onBodyClick);

    if (skipSideEffects) return;

    // Enforce gift qty / stale-gift cleanup on every open
    this.enforceGiftPolicy();

    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  close() {
    this.drawer.setAttribute('aria-hidden', true);
    this.drawer.removeAttribute('aria-expanded', true);
    this.scrollLockClose();

    this.pageOverlayElement.classList.remove('is-visible');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus();
  }

  scrollLockOpen() {
    this.currentScroll = window.scrollY;
    this.siteContainer.style.top = `-${this.currentScroll}px`;
    this.siteHtml.classList.add('scroll-lock');
    this.siteBody.classList.add('scroll-lock');
  }

  scrollLockClose() {
    this.siteContainer.style.top = '';
    this.siteHtml.classList.remove('scroll-lock');
    this.siteBody.classList.remove('scroll-lock');
    if (this.currentScroll !== null) {
      window.scrollTo(0, this.currentScroll);
    }
  }

  // -------------------------------------------------------------------------
  // Optimistic subtotal repaint
  //
  // Sums each line's data-line-price (item.final_price, i.e. unit price after
  // item-level discounts) times its current quantity input value, straight
  // from the DOM — no network round trip. This intentionally does not know
  // about cart-level discount codes, tier-gift additions/removals, etc.; the
  // debounced request in onChange still runs and overwrites this with the
  // authoritative server-rendered value once it lands.
  // -------------------------------------------------------------------------
  updateSubtotalOptimistically(event) {
    if (event.target.getAttribute('name') !== 'updates[]') return;

    const subtotalEl = document.querySelector('.cart-drawer__subtotal');
    if (!subtotalEl || typeof Shopify === 'undefined' || !Shopify.formatMoney) return;

    // Excludes the package protection line — it's priced off Loop's exact
    // computed fee (assets/cart-protection-sync.js), not a value this
    // client-side approximation can reconstruct, and it belongs in its own
    // footer row, not folded into the products subtotal this feeds.
    const items = this.querySelectorAll('.cart-drawer__item[data-line-price]:not([data-protection])');

    // A real cart with a quantity input to change always has at least one
    // matching item — zero matches means the selector didn't find what it
    // expected (stale markup, a render mid-flight, etc.), not an empty
    // cart. Painting $0 in that case is worse than doing nothing and
    // letting the real network response (already in flight) correct it.
    if (!items.length) return;

    // "" (missing/blank attribute) must fail the NaN check below, not
    // silently become 0 — Number('') is 0, not NaN, so a blank data
    // attribute would otherwise zero out the whole total instead of
    // aborting the paint.
    const parseCents = (value) => (value === undefined || value === null || value === '' ? NaN : Number(value));

    let totalCents = 0;
    let originalTotalCents = 0;

    for (const item of items) {
      const unitPrice = parseCents(item.dataset.linePrice);
      // Falls back to unitPrice (not NaN) when the item has no subscription/
      // compare-at price of its own — i.e. its "would have paid" price is
      // just its actual price, contributing no crossed-out difference.
      const unitOriginalPrice = item.dataset.lineOriginalPrice ? parseCents(item.dataset.lineOriginalPrice) : unitPrice;
      const qtyInput = item.querySelector('[name="updates[]"]');
      const quantity = qtyInput
        ? parseCents(qtyInput.value)
        : parseCents(item.querySelector('[data-cart-drawer-quantity]')?.dataset.cartDrawerQuantity);

      if (Number.isNaN(unitPrice) || Number.isNaN(unitOriginalPrice) || Number.isNaN(quantity)) return; // Bail — let the real response paint it.

      totalCents += unitPrice * quantity;
      originalTotalCents += unitOriginalPrice * quantity;
    }

    subtotalEl.textContent = Shopify.formatMoney(totalCents, Shopify.money_format);

    // Update-only: whether the crossed-out row exists at all was decided at
    // render time (which items have subscription pricing doesn't change on
    // a quantity edit), so this just keeps its value in step rather than
    // handling it appearing/disappearing — see patchCartFooter for that.
    const subtotalOriginalEl = document.querySelector('.cart-drawer__subtotal-original');
    if (subtotalOriginalEl) {
      subtotalOriginalEl.textContent = Shopify.formatMoney(originalTotalCents, Shopify.money_format);
    }
  }

  // -------------------------------------------------------------------------
  // Quantity changes
  // -------------------------------------------------------------------------
  onChange(event) {
    this.updateQuantity(
      event.target.dataset.key,
      event.target.value,
      document.activeElement.getAttribute('name')
    );
  }

  // Takes the line item's stable key (not its position in the cart array —
  // see _sendQuantityUpdate for why position is unsafe) so it keeps working
  // even if something else reorders the cart between render and click.
  updateQuantity(key, quantity, name) {
    this.enableLoading(key);
    const requestId = this.beginRequest();
    this._sendQuantityUpdate(key, quantity, requestId, true);
  }

  // Retries once on a failed/non-OK response before giving up — covers
  // transient network hiccups and rate-limited (429) responses from
  // other scripts on the page hammering the cart endpoints.
  //
  // Addresses by line item key (Shopify's /cart/change.js "id" param — a
  // stable identifier), not by array position ("line" param). Position is
  // only valid for the instant the DOM was rendered; assets/cart-protection-
  // sync.js can remove/re-add the protection line between renders (whenever
  // the fee tier changes), which shifts every later line's position and
  // would silently misfire or no-op a position-addressed change.
  _sendQuantityUpdate(key, quantity, requestId, allowRetry) {
    const body = JSON.stringify({
      id: key,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart change request failed: ${response.status}`);
        return response.text();
      })
      .then((state) => {
        if (this.isStaleRequest(requestId)) return;
        const parsedState = JSON.parse(state);

        this.getSectionsToRender().forEach((section) => {
          renderCartSection(section, parsedState.sections[section.section]);
        });

        this.disableLoading();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsedState } }));
      })
      .catch((error) => {
        if (this.isStaleRequest(requestId)) return;

        if (allowRetry) {
          console.warn('[CartDrawer] Quantity update failed, retrying once:', error);
          this._sendQuantityUpdate(key, quantity, requestId, false);
          return;
        }

        console.error('[CartDrawer] Quantity update failed:', error);
        this.disableLoading();
      });
  }

  // Only the most recently issued request is allowed to apply its result —
  // prevents a slower, superseded response from reverting the UI to stale state.
  //
  // Also bumps window.__cartRenderGeneration, a one-directional signal
  // consumed only by assets/cart-protection-sync.js (never read here, so
  // it can't affect this class's own staleness checks above). That
  // script's mutations run on their own async schedule, decoupled from
  // this class's request lifecycle, and its full re-render of the item
  // list could otherwise land after — and silently revert — a more recent
  // action here (e.g. a remove-button click) even though its own data was
  // fetched before that click.
  beginRequest() {
    window.__cartRenderGeneration = (window.__cartRenderGeneration || 0) + 1;
    return ++this._requestId;
  }

  isStaleRequest(requestId) {
    return requestId !== this._requestId;
  }

  // -------------------------------------------------------------------------
  // Sections / HTML helpers
  // -------------------------------------------------------------------------
  getSectionsToRender() {
    return [
      {
        id:       'cart-drawer__content',
        section:  document.getElementById('cart-drawer__content').dataset.id,
        selector: '.cart-drawer__content',
      },
      {
        id:       'cart-icon-bubble',
        section:  'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id:       'cart-drawer__header',
        section:  document.getElementById('cart-drawer__header').dataset.id,
        selector: '.cart-drawer__header',
      },
      {
        id:       'cart-drawer__footer',
        section:  document.getElementById('cart-drawer__footer').dataset.id,
        selector: '.cart-drawer__footer',
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  // -------------------------------------------------------------------------
  // Loading state
  // -------------------------------------------------------------------------
  enableLoading() {
    document.querySelector('#cart-drawer__footer .cart-drawer__footer-content')?.classList.add('is-updating');
  }

  disableLoading() {
    document.querySelector('#cart-drawer__footer .cart-drawer__footer-content')?.classList.remove('is-updating');
  }

  // -------------------------------------------------------------------------
  // Body / cart button click handlers
  // -------------------------------------------------------------------------
  handleBodyClick(evt) {
    const target = evt.target;
    if (!document.querySelector('.js-open-cart') && !target.closest('cart-drawer')) {
      this.close();
    }
  }

  handleCartClick(evt) {
    evt.preventDefault();
    this.open();
  }
}

customElements.define('cart-drawer', CartDrawer);

// ---------------------------------------------------------------------------

/**
 * CartTierBar — auto adds/removes a free gift based on cart threshold.
 *
 * v3 changes vs original:
 *  - _checkIfFreeGift matches on variant ID only (not just the client-forged
 *    _free_tier_gift property) to catch manually-added copies.
 *  - Detects and collapses duplicate gift line items before re-adding once.
 *  - Removes ALL gift lines when the threshold is not met, not just the first.
 *  - Shared gift-policy queue (module-level, also used by CartDrawer) serialises
 *    every instance's check so corrective calls never run concurrently.
 *  - this.isConnected guard prevents ghost calls after re-renders (unchanged).
 */
class CartTierBar extends HTMLElement {
  constructor() {
    super();
    // Small delay lets the surrounding DOM settle after a section re-render.
    // Enqueued on the shared gift-policy queue so this never races with
    // CartDrawer.enforceGiftPolicy or another CartTierBar instance.
    setTimeout(() => {
      enqueueGiftWork(() => {
        // If this element has been removed from the DOM by the time the work
        // runs, skip it to avoid ghost calls on stale elements.
        if (!this.isConnected) return;
        return this._checkIfFreeGift();
      });
    }, 100);
  }

  /**
   * Main logic controller.
   *
   * Matches on variant ID across ALL line items, not just the first one found
   * by .find(). This catches:
   *   • qty > 1 on a single line item
   *   • multiple duplicate line items for the same gift variant
   *   • gift added without the _free_tier_gift property (manual/AJAX add)
   */
  async _checkIfFreeGift() {
    const freeGiftVariantId = this.getAttribute('data-gift-variant-id');
    if (!freeGiftVariantId) return;

    const cartData = await getCartState();
    if (!cartData.items?.length) return;

    const variantIdInt = parseInt(freeGiftVariantId, 10);

    // Match every line item that is the gift variant, regardless of properties
    const allGiftLines = cartData.items.filter((item) => item.id === variantIdInt);

    // The "canonical" gift line — the one that also has _free_tier_gift: true
    const matchingProduct = allGiftLines.find(
      (item) => String(item.properties?._free_tier_gift).toLowerCase() === 'true'
    );

    const isQualified = this.hasAttribute('data-tier-qualified');

    if (isQualified) {
      if (allGiftLines.length > 1) {
        // Duplicate line items — remove all and re-add cleanly as a single qty-1 line
        console.log('[CartTierBar] Duplicate gift lines detected — collapsing');
        for (const line of allGiftLines) {
          await this._cartChange({ id: line.key, quantity: 0 });
        }
        await this._addFreeGift(freeGiftVariantId, { items: [] });
      } else if (matchingProduct) {
        // Exactly one canonical gift line — enforce qty 1
        if (matchingProduct.quantity !== 1) {
          console.log('[CartTierBar] Correcting gift quantity to 1');
          await this._cartChange({ id: matchingProduct.key, quantity: 1 });
        }
      } else {
        // Qualified but no gift present — add it
        await this._addFreeGift(freeGiftVariantId, cartData);
      }
    } else if (allGiftLines.length) {
      // Threshold not met — remove every gift line regardless of properties
      console.log('[CartTierBar] Threshold not met — removing all gift lines');
      for (const line of allGiftLines) {
        await this._cartChange({ id: line.key, quantity: 0 });
      }
    }
  }

  /**
   * Adds the free gift to the cart.
   * Re-checks the cart snapshot for duplicates before calling /cart/add.js.
   */
  async _addFreeGift(variantId, cartData) {
    const alreadyPresent = cartData.items?.some(
      (item) =>
        item.id === parseInt(variantId, 10) &&
        String(item.properties?._free_tier_gift).toLowerCase() === 'true'
    );
    if (alreadyPresent) {
      console.log('[CartTierBar] Gift already present (duplicate guard) — skipping add');
      return;
    }

    console.log('[CartTierBar] Adding free gift', variantId);

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      properties: { _free_tier_gift: 'true' },
      sections: this._getSectionsToRender().map((s) => s.section),
      sections_url: window.location.pathname,
    });

    const response = await fetch('/cart/add.js', {
      method:      'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type':    'application/json',
        'X-Requested-With': 'xmlhttprequest',
      },
      body,
    });

    const result = await response.json();
    if (result.status) throw new Error(result.description);

    this._renderSections(result.sections);
  }

  /**
   * Shared helper for /cart/change.js — handles qty correction and removal.
   */
  async _cartChange(payload) {
    const body = JSON.stringify({
      ...payload,
      sections: this._getSectionsToRender().map((s) => s.section),
      sections_url: window.location.pathname,
    });

    const response = await fetch('/cart/change.js', {
      method:      'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type':    'application/json',
        'X-Requested-With': 'xmlhttprequest',
      },
      body,
    });

    const result = await response.json();
    if (result.status) throw new Error(result.description);

    this._renderSections(result.sections);
  }

  _renderSections(sections) {
    if (!sections) return;
    this._getSectionsToRender().forEach((section) => {
      renderCartSection(section, sections[section.section]);
    });
  }

  _getSectionsToRender() {
    return [
      {
        id:       'cart-drawer__content',
        section:  document.getElementById('cart-drawer__content')?.dataset.id,
        selector: '.cart-drawer__content',
      },
      {
        id:       'cart-icon-bubble',
        section:  'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id:       'cart-drawer__header',
        section:  document.getElementById('cart-drawer__header')?.dataset.id,
        selector: '.cart-drawer__header',
      },
      {
        id:       'cart-drawer__footer',
        section:  document.getElementById('cart-drawer__footer')?.dataset.id,
        selector: '.cart-drawer__footer',
      },
    ];
  }

  _getSectionInnerHTML(html, selector) {
    return (
      new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || ''
    );
  }
}

customElements.define('cart-tier-bar', CartTierBar);

// ---------------------------------------------------------------------------

class CartDiscount extends HTMLElement {
  constructor() {
    super();

    this.input         = this.querySelector('[data-cart-discount-input]');
    this.submit        = this.querySelector('[data-cart-discount-submit]');
    this.store         = window.theme.storefront.store;
    this.apiToken      = window.theme.storefront.apiToken;
    this.removeButtons = this.querySelectorAll('[data-remove-discount]');
    this.errorMessage  = this.querySelector('[data-error-message]');
    this.getCartId();
  }

  async getCartId() {
    this.cartData     = await this.fetchCartId();
    this.cartId       = await this.getCartToken();
    this.cartDiscounts = this.cartData.cart_level_discount_applications.map(
      (discount) => discount.title
    );
    this.init();
  }

  init() {
    this.input.addEventListener('keydown', (event) => {
      if (event.keyCode !== 13) return;
      event.preventDefault();
      this.submitCode();
    });
    this.submit.addEventListener('click', () => { this.submitCode(); });
    this.removeButtons.forEach((removeButton) => {
      removeButton.addEventListener('click', () => {
        this.removeCode(removeButton.dataset.discount);
      });
    });
  }

  async getCartToken() {
    const cookieToken = this.getCookie('cart');
    if (cookieToken) return decodeURIComponent(cookieToken);
    return this.cartData.token;
  }

  async fetchCartId() {
    try {
      return await getCartState();
    } catch (err) {
      console.error(err);
    }
  }

  submitCode() {
    const code = this.input.value;
    if (!code) return;
    const discountCodes = [...this.cartDiscounts];
    discountCodes.push(code);
    this.storefrontDiscountUpdate(discountCodes, code);
  }

  removeCode(code) {
    const discountCodes = [...this.cartDiscounts].filter((discount) => discount !== code);
    this.storefrontDiscountUpdate(discountCodes);
  }

  async storefrontDiscountUpdate(discountCodes, code) {
    this.input.classList.remove('has-error');
    this.errorMessage.classList.add('hidden');
    const url   = `https://${this.store}/api/graphql`;
    const query = `
      mutation CartDiscountCodesUpdate {
        cartDiscountCodesUpdate(
          cartId: "gid://shopify/Cart/${this.cartId}",
          discountCodes: ${JSON.stringify(discountCodes)}
        ) {
          cart {
            id
            discountCodes {
              code
              applicable
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    fetch(url, {
      method:  'POST',
      headers: {
        'Content-Type':                    'application/json',
        'X-Shopify-Storefront-Access-Token': this.apiToken,
      },
      body: JSON.stringify({ query }),
    })
      .then((response) => response.json())
      .then((data) => {
        this.checkValidity(code, data.data.cartDiscountCodesUpdate.cart.discountCodes);
      })
      .catch((error) => console.error('Error:', error));
  }

  checkValidity(enteredCode, codes) {
    if (enteredCode) {
      const codeData = codes.find((code) => code.code === enteredCode);
      if (!codeData.applicable) {
        this.input.classList.add('has-error');
        this.errorMessage.classList.remove('hidden');
        return;
      }
    }
    this.updateSections();
  }

  async updateSections() {
    const sectionsIds = this.getSectionsToRender().map((section) => section.section);
    const url         = `/?sections=${sectionsIds.join(',')}`;

    fetch(url, {
      method:      'GET',
      credentials: 'same-origin',
      headers: {
        'Content-Type':    'application/json',
        'X-Requested-With': 'xmlhttprequest',
      },
    })
      .then((response) => response.json())
      .then((result) => {
        if (result.status) throw new Error(result.description);

        this.getSectionsToRender().forEach((section) => {
          renderCartSection(section, result[section.section]);
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }

  getSectionsToRender() {
    return [
      {
        id:       'cart-drawer__content',
        section:  document.getElementById('cart-drawer__content').dataset.id,
        selector: '.cart-drawer__content',
      },
      {
        id:       'cart-drawer__header',
        section:  document.getElementById('cart-drawer__header').dataset.id,
        selector: '.cart-drawer__header',
      },
      {
        id:       'cart-drawer__footer',
        section:  document.getElementById('cart-drawer__footer').dataset.id,
        selector: '.cart-drawer__footer',
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getCookie(cname) {
    const name = cname + '=';
    const ca   = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1);
      if (c.indexOf(name) === 0) return c.substring(name.length, c.length);
    }
    return '';
  }
}

customElements.define('cart-discount', CartDiscount);

// ---------------------------------------------------------------------------

class CartBundleUpsell extends HTMLElement {
  constructor() {
    super();

    this.button             = this.querySelector('[data-cart-upsell-button]');
    this.parentLineItemKey  = this.dataset.parentLineItemKey;
    this.sellingPlan        = this.dataset.sellingPlan;
    this.kitId              = this.dataset.kitId;
    this.upsellVariantId    = Number(this.dataset.upsellVariantId);
    this.parentLineItem     = {};
    this.otherKitItems      = {};
    this.cart               = this.closest('#cart-drawer');

    this.getCartDetails();
  }

  async getCartDetails() {
    this.cartData     = await this.getCart();
    this.parentLineItem = this.cartData.items.find((item) => item.key === this.parentLineItemKey);

    if (!this.parentLineItem) {
      this.classList.add('hide');
      return;
    }

    this.otherKitItem = this.cartData.items.find(
      (item) =>
        !!item.properties._kitID &&
        item.properties._kitID === this.parentLineItem.properties._kitID &&
        item.product_id !== this.parentLineItem.product_id
    );

    this.init();
  }

  async getCart() {
    try {
      return await getCartState();
    } catch (err) {
      console.error(err);
    }
  }

  async init() {
    this.button.addEventListener('click', () => {
      this.button.disabled = true;
      this.cart && this.cart.classList.add('disable-actions');
      this.modifyCartProducts();
    });
  }

  async modifyCartProducts() {
    const itemsToModify = {};
    itemsToModify[this.parentLineItemKey] = this.parentLineItem.quantity - 1;
    if (this.otherKitItem) itemsToModify[this.otherKitItem.key] = this.otherKitItem.quantity - 1;

    fetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ updates: itemsToModify }),
    })
      .then((response) => response.json())
      .then(() => {
        this.addNewBundledProducts();
      })
      .catch((err) => {
        console.error(err);
      });
  }

  async addNewBundledProducts() {
    const itemsToAdd = [
      {
        id:         this.parentLineItem.id,
        quantity:   1,
        properties: { _kitID: this.kitId },
      },
      {
        id:         this.upsellVariantId,
        quantity:   1,
        properties: { _kitID: this.kitId },
        ...(this.sellingPlan && { selling_plan: this.sellingPlan }),
      },
    ];

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        items: itemsToAdd,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((result) => {
        this.getSectionsToRender().forEach((section) => {
          renderCartSection(section, result.sections[section.section]);
        });
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        this.cart && this.cart.classList.remove('disable-actions');
      });
  }

  getSectionsToRender() {
    return [
      {
        id:       'cart-drawer__content',
        section:  document.getElementById('cart-drawer__content').dataset.id,
        selector: '.cart-drawer__content',
      },
      {
        id:       'cart-icon-bubble',
        section:  'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id:       'cart-drawer__header',
        section:  document.getElementById('cart-drawer__header').dataset.id,
        selector: '.cart-drawer__header',
      },
      {
        id:       'cart-drawer__footer',
        section:  document.getElementById('cart-drawer__footer').dataset.id,
        selector: '.cart-drawer__footer',
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

customElements.define('cart-bundle-upsell', CartBundleUpsell);
